import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowUpRight, Mail, Phone, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'
import { ButtonLink } from '@/components/generals/button'
import { Reveal } from '@/components/motion/reveal'
import { Marquee } from '@/components/motion/marquee'
import { CountUp } from '@/components/motion/count-up'
import { Stars, ReviewCard } from '@/components/widgets/review-card'
import { BeforeAfter } from '@/components/widgets/before-after'
import { FaqAccordion } from '@/components/widgets/faq-accordion'

import {
    topGun,
    topGunMarquee,
    topGunIssues,
    topGunServices,
    topGunSteps,
    topGunAssessment,
    topGunTeam,
    topGunReviews,
    topGunAreas,
    topGunFaqs
} from '@/constants/topgun'

const external = { target: '_blank', rel: 'noopener noreferrer' }

export const TopGunProofSection: FC = (): ReactNode => {
    return (
        <section id='reviews' className='relative py-24 md:py-32'>
            <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                <Reveal className='lg:col-span-4'>
                    <p className='font-mono text-[11px] uppercase tracking-[0.2em] text-slate'>
                        In their own words
                    </p>

                    <div className='mt-6 flex items-end gap-4'>
                        <CountUp
                            value={topGun.rating}
                            decimals={1}
                            className='font-heading text-[6.5rem] font-semibold leading-[0.8] tracking-[-0.06em] text-ink'
                        />

                        <div className='pb-2'>
                            <Stars />

                            <p className='mt-2 font-mono text-[12px] text-slate'>
                                across {topGun.reviewCount} Google reviews
                            </p>
                        </div>
                    </div>

                    <p className='mt-8 max-w-sm text-[15.5px] leading-relaxed text-slate'>
                        Homeowners across Kansas and Missouri trust Top Gun with the concrete at their front door, driveway and backyard.
                    </p>

                    <a href={topGun.reviewsHref} {...external} className='mt-8 inline-flex items-center gap-1.5 text-[15px] font-medium text-ink underline-offset-4 hover:underline'>
                        Read reviews on Google
                        <ArrowUpRight className='size-4' aria-hidden='true' />
                    </a>
                </Reveal>

                <Reveal selector=':scope > *' className='grid gap-4 md:grid-cols-3 lg:col-span-8'>
                    {topGunReviews.map((review, index) => (
                        <ReviewCard key={review.name} review={review} className={cn(index === 1 && 'md:translate-y-8')} />
                    ))}
                </Reveal>
            </Container>

            <div className='mt-24 border-y border-ink/10 py-6 md:mt-32'>
                <Marquee trackClassName='gap-10 pr-10'>
                    {topGunMarquee.map(item => (
                        <span key={item} className='flex items-center gap-10 whitespace-nowrap font-heading text-[2rem] font-semibold tracking-[-0.03em] text-ink md:text-[2.75rem]'>
                            {item}
                            <span className='size-2.5 rotate-45 bg-ember' aria-hidden='true' />
                        </span>
                    ))}
                </Marquee>
            </div>
        </section>
    )
}

export const TopGunIssuesSection: FC = (): ReactNode => {
    return (
        <section id='diagnose' className='bg-sand/60 py-24 md:py-32'>
            <Container>
                <SectionHeading
                    eyebrow="A good place to start"
                    title="Tell us what the concrete is doing."
                    description="Choose the issue that sounds familiar, then share your project with our team in the Easy Estimator."
                />

                <Reveal as='ul' selector=':scope > li' className='mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                    {topGunIssues.map(issue => (
                        <li key={issue.id} className='list-none'>
                            <a
                                href={topGun.estimatorHref}
                                {...external}
                                className='group flex h-full flex-col justify-between gap-10 rounded-[26px] border border-ink/[0.08] bg-card p-7 transition-colors hover:border-ember/40'
                            >
                                <span className='flex items-center justify-between'>
                                    <span className='font-mono text-[12px] text-slate'>{issue.id}</span>
                                    <ArrowUpRight className='size-5 text-slate transition-colors group-hover:text-ember' aria-hidden='true' />
                                </span>

                                <span>
                                    <span className='block font-heading text-[1.35rem] font-semibold tracking-[-0.02em] text-ink'>{issue.title}</span>
                                    <span className='mt-2 block text-[15px] text-slate'>{issue.description}</span>
                                </span>
                            </a>
                        </li>
                    ))}
                </Reveal>
            </Container>
        </section>
    )
}

export const TopGunServicesSection: FC = (): ReactNode => {
    return (
        <section id='services' className='py-24 md:py-32'>
            <Container>
                <SectionHeading
                    eyebrow="Around your home"
                    title="The concrete you know. The support it needs."
                    description="Start with the spot that's giving you trouble. We'll assess whether lifting is right for your concrete."
                    action={(
                        <ButtonLink href='#how-it-works' variant='outline'>
                            See how lifting works
                        </ButtonLink>
                    )}
                />

                <Reveal selector=':scope > article' className='mt-14 grid gap-6 md:grid-cols-2'>
                    {topGunServices.map(service => (
                        <article key={service.number} className='group overflow-hidden rounded-[28px] border border-ink/[0.08] bg-card'>
                            <div className='relative aspect-[4/3] overflow-hidden bg-sand'>
                                <Image
                                    src={service.image}
                                    alt={service.imageAlt}
                                    fill
                                    sizes='(min-width: 768px) 50vw, 100vw'
                                    className='object-cover transition-transform duration-700 ease-expo group-hover:scale-105'
                                />
                            </div>

                            <div className='p-7 sm:p-8'>
                                <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-slate'>
                                    {service.number} · {service.kicker}
                                </p>

                                <h3 className='mt-3 text-h3 font-semibold text-ink'>{service.title}</h3>
                                <p className='mt-3 text-[15.5px] leading-relaxed text-slate'>{service.description}</p>

                                <a href={topGun.estimatorHref} {...external} className='mt-6 inline-flex items-center gap-1.5 text-[15px] font-medium text-ink underline-offset-4 hover:underline'>
                                    Tell us about yours
                                    <ArrowUpRight className='size-4' aria-hidden='true' />
                                </a>
                            </div>
                        </article>
                    ))}
                </Reveal>
            </Container>
        </section>
    )
}

export const TopGunProjectsSection: FC = (): ReactNode => {
    return (
        <section id='recent-work' className='relative overflow-hidden bg-ink py-24 text-cream md:py-32'>
            <Container>
                <SectionHeading
                    tone='light'
                    eyebrow="From the project photos"
                    title="Same concrete. A different welcome."
                    description="Slide across to compare the gap between the landing and the adjoining steps before and after lifting."
                    action={(
                        <ButtonLink href={topGun.galleryHref} variant='outline-light' target='_blank' rel='noopener noreferrer'>
                            Browse every project
                        </ButtonLink>
                    )}
                />

                <Reveal className='mt-14' y={60}>
                    <figure>
                        <BeforeAfter
                            before='/images/topgun/steps-before.webp'
                            after='/images/topgun/steps-after.webp'
                            alt="Entry steps before and after Top Gun concrete lifting"
                            className='mx-auto aspect-[4/5] max-w-3xl rounded-[28px] md:aspect-[8/9] md:rounded-[36px]'
                        />

                        <figcaption className='mx-auto mt-6 max-w-3xl text-[15px] leading-relaxed text-cream/60'>
                            Entry steps, original project photos. Viewpoints and lighting differ between the before and after shots.
                        </figcaption>
                    </figure>
                </Reveal>
            </Container>
        </section>
    )
}

export const TopGunHowItWorksSection: FC = (): ReactNode => {
    return (
        <section id='how-it-works' className='bg-paper py-24 md:py-32'>
            <Container>
                <SectionHeading
                    eyebrow="Beneath the concrete"
                    title="How concrete lifting works."
                    description="Small access holes let us inject expanding polyfoam beneath your slab. It fills the empty space and supports the concrete as it rises."
                />

                <Reveal as='ul' selector=':scope > li' className='mt-14 grid gap-4 md:grid-cols-3'>
                    {topGunSteps.map(step => (
                        <li key={step.number} className='list-none rounded-[26px] border border-ink/[0.08] bg-card p-7 sm:p-8'>
                            <span className='font-heading text-[3rem] font-semibold leading-none tracking-[-0.04em] text-ember'>{step.number}</span>
                            <h3 className='mt-6 text-h3 font-semibold text-ink'>{step.title}</h3>
                            <p className='mt-3 text-[15.5px] leading-relaxed text-slate'>{step.description}</p>
                        </li>
                    ))}
                </Reveal>

                <div className='mt-20 grid gap-10 lg:grid-cols-12'>
                    <div className='lg:col-span-5'>
                        <SectionHeading
                            layout='stack'
                            eyebrow="A clear starting point"
                            title="Every home is different. So is every lift."
                            description="The size, condition, access and amount of settlement shape your repair. Send the details so our team can assess your concrete."
                            action={(
                                <ButtonLink href={topGun.estimatorHref} target='_blank' rel='noopener noreferrer'>
                                    Start a preliminary estimate
                                </ButtonLink>
                            )}
                        />
                    </div>

                    <Reveal as='ul' selector=':scope > li' className='divide-y divide-ink/10 border-y border-ink/10 lg:col-span-7'>
                        {topGunAssessment.map(item => (
                            <li key={item.number} className='flex list-none gap-6 py-6'>
                                <span className='font-mono text-[12px] text-slate'>{item.number}</span>
                                <span>
                                    <span className='block font-heading text-[1.25rem] font-semibold tracking-[-0.02em] text-ink'>{item.title}</span>
                                    <span className='mt-1 block text-[15px] text-slate'>{item.description}</span>
                                </span>
                            </li>
                        ))}
                    </Reveal>
                </div>
            </Container>
        </section>
    )
}

export const TopGunGuaranteeSection: FC = (): ReactNode => {
    return (
        <section id='guarantee' className='py-24 md:py-32'>
            <Container>
                <Reveal className='grid items-center gap-10 overflow-hidden rounded-[36px] bg-ember p-8 text-cream sm:p-12 lg:grid-cols-2 lg:p-16'>
                    <div>
                        <p className='font-mono text-[11px] uppercase tracking-[0.2em] text-cream/70'>Standing behind the lift</p>
                        <h2 className='mt-5 max-w-[16ch] text-h2 font-semibold text-balance'>A promise for the place you call home.</h2>
                        <p className='mt-6 max-w-md text-[15.5px] leading-relaxed text-cream/80'>
                            Our guarantee is part of the repair. The terms and conditions are in your contract.
                        </p>

                        <div className='mt-8'>
                            <ButtonLink href={topGun.phoneHref} variant='light' icon='phone'>
                                Talk it through with our team
                            </ButtonLink>
                        </div>
                    </div>

                    <div className='flex items-center gap-6 lg:justify-end'>
                        <ShieldCheck className='size-6 shrink-0 text-cream/70' aria-hidden='true' />
                        <div>
                            <p className='font-heading text-[6rem] font-semibold leading-[0.85] tracking-[-0.06em] md:text-[8rem]'>
                                {topGun.guaranteeYears}
                                <span className='ml-2 align-top font-mono text-[14px] tracking-[0.2em]'>YEARS</span>
                            </p>
                            <p className='mt-4 font-heading text-[1.4rem] font-semibold tracking-[-0.02em]'>If it sinks again, we'll lift it again.</p>
                            <p className='mt-2 text-[13.5px] text-cream/70'>Subject to the terms and conditions in your contract.</p>
                        </div>
                    </div>
                </Reveal>
            </Container>
        </section>
    )
}

export const TopGunTeamSection: FC = (): ReactNode => {
    return (
        <section id='team' className='bg-sand/60 py-24 md:py-32'>
            <Container>
                <SectionHeading
                    eyebrow="People, not just equipment"
                    title="Your Top Gun team."
                    description="Also on your team: Sabrina, Concrete Consultant, KCMO."
                    action={(
                        <ButtonLink href={topGun.aboutHref} variant='outline' target='_blank' rel='noopener noreferrer'>
                            More about Top Gun
                        </ButtonLink>
                    )}
                />

                <Reveal as='ul' selector=':scope > li' className='mt-14 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5'>
                    {topGunTeam.map(member => (
                        <li key={member.name} className='list-none'>
                            <div className='relative aspect-square overflow-hidden rounded-[24px] bg-sand'>
                                <Image src={member.image} alt={`Portrait of ${member.name}`} fill sizes='(min-width: 1024px) 20vw, 50vw' className='object-cover' />
                            </div>
                            <p className='mt-4 font-heading text-[1.2rem] font-semibold tracking-[-0.02em] text-ink'>{member.name}</p>
                            <p className='mt-1 text-[14px] text-slate'>{member.role}</p>
                        </li>
                    ))}
                </Reveal>
            </Container>
        </section>
    )
}

export const TopGunAreasSection: FC = (): ReactNode => {
    const groups = [
        { state: "Kansas", towns: topGunAreas.kansas },
        { state: "Missouri", towns: topGunAreas.missouri }
    ]

    return (
        <section id='service-area' className='py-24 md:py-32'>
            <Container>
                <SectionHeading
                    eyebrow="Kansas & Missouri"
                    title="A little closer to home."
                    description="Serving homeowners across these communities. Call to confirm your project address."
                    action={(
                        <ButtonLink href={topGun.phoneHref} variant='outline' icon='phone'>
                            Confirm your coverage
                        </ButtonLink>
                    )}
                />

                <Reveal selector=':scope > div' className='mt-14 grid gap-4 md:grid-cols-2'>
                    {groups.map(group => (
                        <div key={group.state} className='rounded-[28px] border border-ink/[0.08] bg-card p-7 sm:p-8'>
                            <p className='font-mono text-[11px] uppercase tracking-[0.2em] text-ember'>{group.state}</p>
                            <ul className='mt-6 flex flex-wrap gap-2'>
                                {group.towns.map(town => (
                                    <li key={town} className='rounded-full border border-ink/10 px-4 py-2 text-[15px] text-ink'>{town}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </Reveal>

                <p className='mt-6 text-center font-mono text-[12px] uppercase tracking-[0.16em] text-slate'>Two states. One Top Gun team.</p>
            </Container>
        </section>
    )
}

export const TopGunFaqSection: FC = (): ReactNode => {
    return (
        <section id='faq' className='bg-paper py-24 md:py-32'>
            <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:col-span-5'>
                    <div className='lg:sticky lg:top-32'>
                        <SectionHeading
                            layout='stack'
                            eyebrow="A few good questions"
                            title="Your questions, answered."
                            description="Understand the repair, then talk with us about your concrete."
                            action={(
                                <ButtonLink href={topGun.phoneHref} variant='outline' icon='phone'>
                                    Call Top Gun
                                </ButtonLink>
                            )}
                        />
                    </div>
                </div>

                <Reveal className='lg:col-span-7'>
                    <FaqAccordion items={topGunFaqs} />
                </Reveal>
            </Container>
        </section>
    )
}

export const TopGunContactSection: FC = (): ReactNode => {
    const steps = [
        { number: '01', title: "Describe the surface", description: "Choose the concrete areas that need attention." },
        { number: '02', title: "Show us what's changed", description: "Follow the guided prompts to share project photos." },
        { number: '03', title: "Let's assess the repair", description: "Our team reviews the details for a preliminary estimate." }
    ]

    return (
        <section id='contact' className='bg-ink py-24 text-cream md:py-32'>
            <Container className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:col-span-6'>
                    <SectionHeading
                        tone='light'
                        layout='stack'
                        eyebrow="Your next step"
                        title="Show us the concrete. Let's talk about the lift."
                        description="A few details and clear photos help us understand your project. Start with our Easy Estimator."
                        action={(
                            <div className='flex flex-wrap gap-3'>
                                <ButtonLink href={topGun.estimatorHref} size='lg' target='_blank' rel='noopener noreferrer'>
                                    Open the Easy Estimator
                                </ButtonLink>
                                <ButtonLink href={topGun.phoneHref} variant='glass' size='lg' icon='phone'>
                                    Call {topGun.phone}
                                </ButtonLink>
                            </div>
                        )}
                    />

                    <ul className='mt-10 space-y-3 text-[15px] text-cream/75'>
                        <li className='flex items-center gap-3'>
                            <Phone className='size-4 text-ember-soft' aria-hidden='true' />
                            Local: <a href={topGun.localPhoneHref} className='text-cream underline-offset-4 hover:underline'>{topGun.localPhone}</a>
                        </li>
                        <li className='flex items-center gap-3'>
                            <Mail className='size-4 text-ember-soft' aria-hidden='true' />
                            <a href={topGun.emailHref} className='text-cream underline-offset-4 hover:underline'>{topGun.email}</a>
                        </li>
                    </ul>
                </div>

                <Reveal as='ul' selector=':scope > li' className='space-y-4 lg:col-span-6'>
                    {steps.map(step => (
                        <li key={step.number} className='flex list-none gap-6 rounded-[24px] border border-cream/10 bg-cream/[0.04] p-6'>
                            <span className='font-mono text-[12px] text-ember-soft'>{step.number}</span>
                            <span>
                                <span className='block font-heading text-[1.25rem] font-semibold tracking-[-0.02em]'>{step.title}</span>
                                <span className='mt-1 block text-[15px] text-cream/65'>{step.description}</span>
                            </span>
                        </li>
                    ))}
                </Reveal>
            </Container>
        </section>
    )
}
